import { Loop } from '../engine/core/Loop';
import { Scene, Entity } from '../engine/core/Scene';
import { Transform } from '../engine/core/Transform';
import { Input } from '../engine/input/Input';
import { Camera } from '../engine/render/Camera';
import { Renderer, DrawItem, FrameParams, defaultFrameParams } from '../engine/render/Renderer';
import { GpuMesh } from '../engine/render/GpuMesh';
import { StaticWorld } from '../engine/physics/StaticWorld';
import { TriggerSystem } from '../engine/physics/Triggers';
import { Tweens } from '../engine/anim/Tween';
import { MeshData } from '../engine/geo/mesh';
import { Ctx, Renderable, makeRenderable } from './Ctx';
import { Sfx } from './audio/Sfx';
import { ThirdPersonCamera } from './camera/ThirdPersonCamera';
import { Player } from './player/Player';
import { World, Mode } from './World';
import { Debug } from './debug/Debug';
import { Screens } from './ui/Screens';
import { hasSave, loadSave, clearSave } from './world/Save';
import { SPOTS } from './world/Layout';

type GameState = 'title' | 'playing' | 'paused' | 'won';
const SETTINGS_KEY = 'threshold.settings';
const START_KEY = 'threshold.start';

export class Game implements Ctx {
  readonly renderer: Renderer;
  readonly input: Input;
  readonly camera = new Camera();
  readonly loop: Loop;
  readonly scene = new Scene();
  readonly transforms = this.scene.store<Transform>();
  readonly renderables = this.scene.store<Renderable>();
  readonly statics = new StaticWorld();
  readonly triggers = new TriggerSystem();
  readonly tweens = new Tweens();
  readonly sfx: Sfx;
  readonly frame: FrameParams;
  time = 0;
  readonly tpc: ThirdPersonCamera;
  player!: Player;
  world!: World;
  readonly debug: Debug;
  readonly screens: Screens;
  state: GameState = 'title';
  private readonly drawList: DrawItem[] = [];
  private readonly fpsEl: HTMLDivElement;
  private hitStopTimer: number | null = null;
  readonly fx: Ctx['fx'];
  private settings = { sensitivity: 0.0022, volume: 0.8 };
  private prevLocked = false;

  constructor(readonly canvas: HTMLCanvasElement, readonly ui: HTMLDivElement) {
    this.renderer = new Renderer(canvas);
    this.input = new Input(canvas);
    this.sfx = new Sfx();
    this.frame = defaultFrameParams(this.camera);
    this.loop = new Loop((dt) => this.update(dt), (alpha, dt) => this.render(alpha, dt), 60);
    this.tpc = new ThirdPersonCamera(this);
    this.screens = new Screens(ui);
    this.fpsEl = document.createElement('div');
    this.fpsEl.className = 'fps';
    ui.appendChild(this.fpsEl);
    window.addEventListener('resize', () => this.renderer.resize());
    canvas.addEventListener('click', () => { if (this.state === 'playing') this.input.requestLock(); this.sfx.resume(); });
    window.addEventListener('keydown', () => this.sfx.resume(), { once: true });
    window.addEventListener('pointerdown', () => this.sfx.resume(), { once: true });
    this.fx = {
      shake: (a, d) => this.tpc.shake(a, d),
      fovKick: (d) => this.tpc.fovKick(d),
      hitStop: (s) => this.hitStop(s),
      flashWhite: (a) => { this.frame.post.white = a; },
    };
    this.loadSettings();

    // what to start: a title-screen choice stored before reload, a debug URL, or the title itself
    const params = new URLSearchParams(location.search);
    let start = params.get('mode') ?? (params.get('debug') === '1' ? 'A' : null);
    try { const s = localStorage.getItem(START_KEY); if (s) { start = s; localStorage.removeItem(START_KEY); } } catch { /* ignore */ }
    const save = start === 'continue' ? loadSave() : null;
    const mode: Mode = save ? save.mode : start === 'B' ? 'B' : 'A';
    this.buildWorld(mode, save);
    this.debug = new Debug(this);
    if (start) this.play(); else this.showTitle();
  }

  private loadSettings(): void {
    try { const s = localStorage.getItem(SETTINGS_KEY); if (s) Object.assign(this.settings, JSON.parse(s)); } catch { /* ignore */ }
    this.tpc.sensitivity = this.settings.sensitivity;
    this.sfx.synth.setVolume(this.settings.volume);
  }
  private saveSettings(): void {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings)); } catch { /* ignore */ }
  }

  spawn(name: string, t: Transform, mesh?: MeshData | GpuMesh, opts: Partial<Renderable> = {}): Entity {
    const e = this.scene.create(name);
    t.entity = e;
    this.transforms.set(e, t);
    if (mesh) {
      const gm = mesh instanceof GpuMesh ? mesh : this.renderer.createMesh(mesh, name);
      this.renderables.set(e, makeRenderable(gm, opts));
    }
    return e;
  }

  private hitStop(seconds: number): void {
    if (this.state !== 'playing') return;
    this.loop.timeScale = 0;
    if (this.hitStopTimer !== null) clearTimeout(this.hitStopTimer);
    this.hitStopTimer = window.setTimeout(() => { if (this.state === 'playing') this.loop.timeScale = 1; this.hitStopTimer = null; }, seconds * 1000);
  }

  private buildWorld(mode: Mode, save: ReturnType<typeof loadSave>): void {
    this.world = new World(this, this.tpc, this.ui, mode);
    this.world.build(save);
    this.player = this.world.player;
    this.world.onWin = (stats) => {
      this.state = 'won';
      clearSave();
      this.input.releaseLock();
      this.world.hud.setVisible(false);
      this.screens.win({ ...stats, mode: this.world.mode }, (m) => this.restart(m));
    };
  }

  private restart(mode: Mode | 'continue'): void {
    try { localStorage.setItem(START_KEY, mode); } catch { /* ignore */ }
    location.reload();
  }

  showTitle(): void {
    this.state = 'title';
    this.world.hud.setVisible(false);
    this.input.releaseLock();
    this.input.enabled = false;
    this.screens.title({
      hasSave: hasSave(),
      sensitivity: this.settings.sensitivity,
      volume: this.settings.volume,
      onNew: (m) => { this.sfx.uiClick(); if (this.world.mode === m && this.world.elapsed < 1) this.play(); else this.restart(m); },
      onContinue: () => { this.sfx.uiClick(); this.restart('continue'); },
      onSensitivity: (v) => { this.settings.sensitivity = v; this.tpc.sensitivity = v; this.saveSettings(); },
      onVolume: (v) => { this.settings.volume = v; this.sfx.synth.setVolume(v); this.saveSettings(); },
    });
  }

  play(): void {
    this.screens.hide();
    this.state = 'playing';
    this.loop.timeScale = 1;
    this.input.enabled = true;
    this.world.hud.setVisible(true);
    this.tpc.snapTo(this.player.position);
    this.input.requestLock();
  }

  pause(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.loop.timeScale = 0;
    this.input.releaseLock();
    this.screens.pause(this.world.items.held?.def.name ?? null, () => this.play(), () => { this.world.save(); location.reload(); }, this.settings.sensitivity, (v) => { this.settings.sensitivity = v; this.tpc.sensitivity = v; this.saveSettings(); });
  }

  start(): void { this.loop.start(); }

  private update(dt: number): void {
    this.input.pollGamepad();
    // Esc is swallowed by the browser when it exits pointer lock, so losing the lock also pauses
    const lostLock = this.prevLocked && !this.input.locked && !this.input.useVirtual;
    this.prevLocked = this.input.locked;
    if (this.state === 'playing' && (this.input.wasPressed('Escape') || lostLock)) { this.pause(); this.input.endStep(); return; }
    if (this.state === 'paused' && this.input.wasPressed('Escape')) { this.play(); this.input.endStep(); return; }
    if (this.state === 'title') {
      // slow orbit of the Hearth as a backdrop
      this.time += dt;
      for (const t of this.transforms.values()) if (!t.parent) snapTree(t);
      const a = this.time * 0.08;
      this.camera.position.set(SPOTS.goldKeep.x + Math.cos(a) * 42, 14, SPOTS.goldKeep.z + Math.sin(a) * 42 + 10);
      this.camera.target.set(SPOTS.goldKeep.x, 6, SPOTS.goldKeep.z);
      this.camera.fov = 55;
      this.frame.shadowCenter.copy(this.camera.target);
      this.world.particles.update(dt);
      this.tweens.update(dt);
      this.input.endStep();
      this.scene.flush();
      return;
    }
    this.time += dt;
    for (const t of this.transforms.values()) if (!t.parent) snapTree(t);
    const [mdx, mdy] = this.input.takeMouse();
    const look = this.input.locked || this.input.useVirtual || this.input.pad.connected;
    if (look && !this.debug.freeFly) this.tpc.look(mdx, mdy);
    this.debug.update(dt);
    this.player.update(dt);
    this.triggers.update(this.player.entity, this.player.position, dt);
    this.world.update(dt);
    this.tweens.update(dt);
    this.tpc.update(this.player.position, dt);
    if (this.debug.freeFly) this.debug.applyCamera(look ? mdx : 0, look ? mdy : 0);
    const fade = this.tpc.boomNow < 0.8 ? 0.3 : 1;
    for (const r of this.player.parts) { r.alpha = fade; r.transparent = fade < 1; }
    this.sfx.listener.copy(this.camera.position);
    this.sfx.listenerRight.copy(this.tpc.right);
    this.frame.shadowCenter.copy(this.player.position);
    this.input.endStep();
    this.scene.flush();
  }

  private render(alpha: number, _dt: number): void {
    this.renderer.resize();
    this.frame.time = this.time + alpha * this.loop.stepDt;
    this.drawList.length = 0;
    for (const [e, t] of this.transforms) {
      if (t.parent) continue;
      t.updateWorld(alpha);
      this.collect(e, t);
    }
    this.renderer.render(this.drawList, this.frame);
    if ((this.loop.frameCount & 15) === 0) {
      const s = this.renderer.stats;
      const p = this.player.position;
      this.fpsEl.textContent = this.debug.enabled ? `${this.loop.fps.toFixed(0)} fps · ${s.drawCalls} dc · ${(s.triangles / 1000).toFixed(1)}k tri · ${s.culled} culled · ${p.x.toFixed(1)},${p.y.toFixed(1)},${p.z.toFixed(1)}` : '';
    }
  }

  private collect(e: number, t: Transform): void {
    if (!t.visible) return;
    const rd = this.renderables.get(e);
    if (rd && rd.visible) {
      this.drawList.push({ mesh: rd.mesh, model: t.world, emissiveMul: rd.emissiveMul, flash: rd.flash, alpha: rd.alpha, wind: rd.wind, castShadow: rd.castShadow, transparent: rd.transparent, instanced: rd.instanced, noCull: rd.noCull, ambientMul: rd.ambientMul });
    }
    for (const c of t.children) if (c.entity >= 0) this.collect(c.entity, c);
  }
}

function snapTree(t: Transform): void {
  t.snapPrev();
  for (const c of t.children) snapTree(c);
}
